/**
 * @file blog-options.ts
 * @description Вид ленты и статьи блога (phase19.16 Р2; SB39, SB40, SB61,
 * SB70; db-design-r2 §5): сетка/список, обложки, что показывать в карточке,
 * статей на странице, шаблон статьи (широкая обложка / узкая / без обложки),
 * оглавление, «поделиться», блок автора, «Похожие статьи»; обложка встроенной
 * шапки ленты — фоном во всю ширину или картинкой сбоку (решение пользователя
 * 2026-10-12, партия после приёмки Р2а).
 *
 * Хранится в `SiteDraft.blogOptions` (JSON, `{}` по умолчанию) и публикуется
 * вместе с видом сайта внутри `SiteRevision.blog.options` (часть BLOG, право
 * контента). Версия схемы вида не меняется: новые ключи приходят умолчанием
 * при чтении (`readBlogOptions`), битое поле заменяется только оно само.
 */

import { z } from "zod";

import { sectionFocusSchema } from "../site-section/schema.js";
import { cloneJson, repairBySchema, type RepairIssue } from "./repair.js";
import { mergeMissingKeys } from "./schema.js";

/** Статей на странице ленты. */
export const SITE_BLOG_PER_PAGE = [6, 9, 12, 18] as const;

export const siteBlogOptionsSchema = z.strictObject({
  /**
   * Шапка ленты (решение пользователя 2026-10-11): builtin — встроенная (как
   * до Р2); custom — встроенная не рисуется, владелец ставит свою секцию в
   * место «Над лентой блога» (заготовка `blog-feed-header`). Сетка статей — системная.
   */
  feedHeader: z.enum(["builtin", "custom"]),
  /**
   * Как показать обложку встроенной шапки ленты (решение пользователя
   * 2026-10-12): background — фоном во всю ширину под заголовком (умолчание,
   * его и обещала подсказка кабинета); side-right / side-left — картинкой
   * сбоку от текста. При `feedHeader: "custom"` не используется.
   */
  coverLayout: z.enum(["background", "side-right", "side-left"]),
  /**
   * Точка фокуса обложки в режиме «фоном» (object-position в процентах 0–100) —
   * та же схема, что у фото-фона секции (sectionFocusSchema): кабинет
   * переиспользует один выбор точки. Решение 2026-10-12.
   */
  coverFocus: sectionFocusSchema,
  /**
   * Затемнение обложки в режиме «фоном», доля 0–0,6 — диапазон как у `dim`
   * фото-фона секции и картинки фона страницы в теме (один ползунок на всё).
   * Решение 2026-10-12.
   */
  coverDim: z.number().min(0).max(0.6),
  /**
   * Тон текста встроенной шапки поверх обложки в режиме «фоном» — те же
   * значения, что `textTone` фото-фона секции; в режимах «сбоку» не действует.
   * Решение 2026-10-12.
   */
  coverTextTone: z.enum(["light", "dark"]),
  feedLayout: z.enum(["grid2", "grid3", "list"]),
  /** Обложки статей в ленте. */
  feedCovers: z.boolean(),
  cardFields: z.strictObject({
    category: z.boolean(),
    author: z.boolean(),
    date: z.boolean(),
    readTime: z.boolean(),
    excerpt: z.boolean(),
  }),
  perPage: z.union([z.literal(6), z.literal(9), z.literal(12), z.literal(18)]),
  /** Шаблон статьи: широкая обложка, узкая обложка, без обложки. */
  articleLayout: z.enum(["wide", "narrow", "none"]),
  /** Оглавление статьи: сбоку или над текстом. */
  toc: z.strictObject({ on: z.boolean(), place: z.enum(["side", "top"]) }),
  share: z.boolean(),
  authorBlock: z.boolean(),
  related: z.strictObject({ on: z.boolean(), count: z.number().int().min(2).max(6) }),
});

export type SiteBlogOptions = z.infer<typeof siteBlogOptionsSchema>;

/**
 * Умолчание платформы — как витрина рисовала блог до Р2 (принцип «Как было»,
 * сверено с HEAD apps/web 2026-10-10): лента — сетка в 3 колонки, обложки,
 * 12 статей на странице; карточка — рубрика, анонс, дата, время чтения, без
 * автора; статья — широкая обложка, оглавление над текстом (ArticleTableOfContents
 * при 3+ подзаголовках), «Поделиться», 3 похожие статьи. Отдельного блока
 * автора не было (автор — строкой в шапке статьи), поэтому authorBlock = false.
 * Исключение — обложка шапки ленты (решение пользователя 2026-10-12): умолчание
 * «фоном во всю ширину», хотя до решения витрина рисовала её картинкой справа.
 */
export const DEFAULT_SITE_BLOG_OPTIONS: Readonly<SiteBlogOptions> = Object.freeze({
  feedHeader: "builtin",
  // Решение 2026-10-12: обложка фоном во всю ширину, фокус по центру, затемнение
  // 40 % (ступень фото-фона секции 0/20/40/60) — чтобы заголовок и подзаголовок читались поверх любого фото.
  coverLayout: "background",
  coverFocus: Object.freeze({ x: 50, y: 50 }),
  coverDim: 0.4,
  // Светлый текст поверх затемнённого фото — как у фото-фона секции по умолчанию.
  coverTextTone: "light",
  feedLayout: "grid3",
  feedCovers: true,
  // ArticleCard сегодня: рубрика, анонс, дата и время чтения; автора в карточке нет.
  cardFields: Object.freeze({ category: true, author: false, date: true, readTime: true, excerpt: true }),
  perPage: 12,
  articleLayout: "wide",
  toc: Object.freeze({ on: true, place: "top" }),
  share: true,
  authorBlock: false,
  related: Object.freeze({ on: true, count: 3 }),
}) as Readonly<SiteBlogOptions>;

export interface SiteBlogOptionsReadResult {
  options: SiteBlogOptions;
  replaced: RepairIssue[];
}

/** Чтение `SiteDraft.blogOptions` / `SiteRevision.blog.options`: недостающее — умолчанием, битое — по полям. */
export function readBlogOptions(raw: unknown): SiteBlogOptionsReadResult {
  const replaced: RepairIssue[] = [];
  const defaults = cloneJson(DEFAULT_SITE_BLOG_OPTIONS) as SiteBlogOptions;
  const merged = mergeMissingKeys(defaults, raw ?? {});
  const direct = siteBlogOptionsSchema.safeParse(merged);
  if (direct.success) return { options: direct.data, replaced };
  const repaired = siteBlogOptionsSchema.safeParse(repairBySchema(siteBlogOptionsSchema, merged, defaults, "", replaced));
  if (repaired.success) return { options: repaired.data, replaced };
  replaced.push({ path: "", reason: "Параметры блога не прошли проверку — взяты умолчания" });
  return { options: defaults, replaced };
}
